import React, { useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '../Button';
import { localTimeZone, timeZoneLabel } from '../../utils/time';
import type { TrafficPoint } from '../../types/traffic';
interface TrafficVolumeChartProps {
  series: TrafficPoint[];
}
type Zone = 'utc' | 'local';
const axisStyle = {
  fill: 'var(--muted-foreground)',
  fontSize: 11,
  fontFamily: 'Geist Mono'
};
function viewerZone(): string {
  return localTimeZone();
}

/** Standard-time abbreviation for the viewer, e.g. 'EST'. */
function viewerZoneLabel(): string {
  return timeZoneLabel(localTimeZone());
}
function localLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function TrafficVolumeChart({
  series
}: TrafficVolumeChartProps) {
  const [zone, setZone] = useState<Zone>('utc');
  const zoneLabel = viewerZoneLabel();
  const isUtcViewer = viewerZone() === 'UTC' || zoneLabel === 'UTC';
  const canShowLocal = !isUtcViewer && series.some(point => Boolean(point.bucketStart));

  // Each point carries both labels so the axis and tooltip stay in step.
  const data = useMemo(() => series.map(point => ({
    ...point,
    localTime: point.bucketStart ? localLabel(point.bucketStart) : point.time,
    utcTime: point.time
  })), [series]);
  const activeKey = zone === 'local' && canShowLocal ? 'localTime' : 'utcTime';
  return <div data-id="element-577" data-mp="39o273vlgmw71">
      {canShowLocal && <div className="mb-3 flex items-center gap-1" role="group" aria-label="Time zone for the horizontal axis" data-id="element-578" data-mp="3nocyr0ulget9">
          <Button variant={zone === 'utc' ? 'secondary' : 'ghost'} size="sm" onClick={() => setZone('utc')} aria-pressed={zone === 'utc'} data-id="element-579" data-mp="3ipt6kipeq6sg">
            UTC
          </Button>
          <Button variant={zone === 'local' ? 'secondary' : 'ghost'} size="sm" onClick={() => setZone('local')} aria-pressed={zone === 'local'} data-id="element-580" data-mp="1odlfxhk4cc2h">
            {zoneLabel}
          </Button>
        </div>}

      <div className="h-60 w-full sm:h-72" data-id="element-581" data-mp="1791kl3lswmt">
        <ResponsiveContainer width="100%" height="100%" data-id="element-582" data-mp="67na0494a8ld">
          <AreaChart data={data} margin={{
          top: 8,
          right: 8,
          bottom: 0,
          left: -12
        }} data-id="element-583" data-mp="696p3rj1edlk">
            <defs data-id="element-584" data-mp="b6gnvxsbg1m2">
              <linearGradient id="requestsFill" x1="0" y1="0" x2="0" y2="1" data-id="element-585" data-mp="b4vtc8604io7">
                <stop offset="0%" stopColor="var(--chart-3)" stopOpacity={0.28} data-id="element-586" data-mp="g4i66ghx3wwh" />
                <stop offset="100%" stopColor="var(--chart-3)" stopOpacity={0.02} data-id="element-587" data-mp="kxuh5ntoc56r" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} data-id="element-588" data-mp="oxlde71yzdio" />
            <XAxis dataKey={activeKey} tick={axisStyle} stroke="var(--border)" tickMargin={8} data-id="element-589" data-mp="tz0yfz45dk12" />
            <YAxis tick={axisStyle} stroke="var(--border)" tickFormatter={value => `${Math.round(Number(value) / 1000)}k`} data-id="element-590" data-mp="yx2hwl5hjld2" />
            <Tooltip contentStyle={{
            background: 'var(--popover)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            fontSize: 12,
            color: 'var(--popover-foreground)'
          }} formatter={(value: number) => value.toLocaleString()}
          // Always show BOTH zones in the tooltip, whichever axis is active.
          labelFormatter={(_label, payload) => {
            const point = payload?.[0]?.payload as {
              utcTime: string;
              localTime: string;
            } | undefined;
            if (!point) return '';
            if (!canShowLocal) return `${point.utcTime} UTC`;
            return `${point.localTime} ${zoneLabel} · ${point.utcTime} UTC`;
          }} data-id="element-591" data-mp="140d33b5qv54p" />
            <Legend wrapperStyle={{
            fontSize: 12,
            color: 'var(--muted-foreground)',
            paddingTop: 8
          }} data-id="element-592" data-mp="25onmnjnxek73" />
            <Area type="monotone" dataKey="requests" name="Requests" stroke="var(--chart-3)" strokeWidth={2} fill="url(#requestsFill)" data-id="element-593" data-mp="1v7k54x8bn2rh" />
            <Line type="monotone" dataKey="uniqueVisitors" name="Unique visitors" stroke="var(--chart-2)" strokeWidth={2} dot={false} data-id="element-594" data-mp="1lafdwizmch3t" />
            <Line type="monotone" dataKey="retries" name="Retries" stroke="var(--chart-5)" strokeWidth={2} strokeDasharray="4 4" dot={false} data-id="element-595" data-mp="2e0fjyl5uw089" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
}